import { SetStateAction, Dispatch, ChangeEvent, useState } from 'react';
import {
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubDynamicIcon,
  devhubMakeStyles,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import useDebounce from 'react-use/lib/useDebounce';

const IOPSupportOwnersQuery = gql`
  query getIOPSupportOwners($filter: ResourceCatalogSupportOwnerFilter) {
    resources {
      iopSupportOwners(filter: $filter)
    }
  }
`;

const IOPSupportGroupsQuery = gql`
  query getIOPSupportGroups($filter: ResourceCatalogSupportGroupFilter) {
    resources {
      iopSupportGroups(filter: $filter)
    }
  }
`;

const useStyles = devhubMakeStyles({
  search: {
    marginTop: '0.25rem',
  },
});

interface Props {
  groupEnabled?: boolean;
  helperText?: string;
  className?: any;
  name?: string;
  setSelectedParty: Dispatch<SetStateAction<string>>;
  selectedParty?: string;
  required?: boolean;
}

/**
 * Search for a party or group bases on prop isOwner
 * @param props Props of the component
 * @param props.groupEnabled Set to true to allow searching of groups and enabled the toggle
 *
 */
export function IOPSearch({
  groupEnabled = false,
  setSelectedParty,
  className,
  helperText,
  selectedParty = '',
  required = false,
}: Props) {
  const [owners, setOwners] = useState<Array<string>>([]);
  const [groups, setGroups] = useState<Array<string>>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const classes = useStyles();

  const [getOwners, { loading: ownersLoading, error: ownersError }] =
    useLazyQuery(IOPSupportOwnersQuery, {
      errorPolicy: 'all',
    });

  const [getGroups, { loading: groupsLoading, error: groupsError }] =
    useLazyQuery(IOPSupportGroupsQuery, {
      errorPolicy: 'all',
    });

  useDebounce(
    () => {
      if (inputValue && !groupEnabled) {
        getOwners({
          variables: { filter: { ownerName: `*${inputValue}*` } },
        }).then(ownerResults => {
          setOwners(ownerResults?.data?.resources?.iopSupportOwners);
        });
      } else if (inputValue && groupEnabled) {
        getGroups({
          variables: { filter: { groupName: `*${inputValue}*` } },
        }).then(groupResults => {
          if (groupResults?.data?.resources?.iopSupportGroups?.length > 0) {
            setGroups(groupResults.data.resources.iopSupportGroups);
          }
        });
      }
    },
    500,
    [inputValue],
  );

  // Event Handler to set selected party from a drop down
  const onSelect = (
    _event: Object,
    value: string | null,
    reason: DevHubAutocompleteChangeReason,
  ) => {
    if (reason === 'clear' && !value) {
      setSelectedParty('clear');
    }
    if (!value) {
      return;
    }
    setSelectedParty(value);
  };

  // Event Handler for the text box and to trigger the query after 3 keystrokes
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
  };

  const partyType = () => {
    return groupEnabled ? 'IOP Support Group' : 'IOP CI Owner Group';
  };

  const partyText = () => {
    return groupEnabled ? 'Support Group' : 'CI Owner Group';
  };

  return (
    <>
      {ownersError || groupsError ? (
        <DevHubTextField
          className={className}
          error
          label="Error"
          defaultValue={`No ${partyType()} Available`}
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          className={className}
          loading={ownersLoading || groupsLoading}
          loadingText="Loading.."
          options={groupEnabled ? groups : owners}
          onChange={onSelect}
          defaultValue={selectedParty?.length > 0 ? selectedParty : undefined}
          getOptionLabel={option => String(option)}
          renderInput={params => (
            <DevHubTextField
              {...params}
              label={`${partyType()}`}
              InputLabelProps={{ required: required }}
              helperText={helperText ?? `${partyText()}`}
              margin="normal"
              variant="outlined"
              required={required}
              onChange={onChange}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <span className={classes.search}>
                      <DevHubDynamicIcon iconName="Search" />
                    </span>
                  </>
                ),
              }}
            />
          )}
        />
      )}
    </>
  );
}
